import React, { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { t } from '../utils/translations'
import MaintenanceBanner from './MaintenanceBanner'
import AppBranding from './AppBranding'
import ThemeToggle from './ThemeToggle'

const WEEKS_STORAGE_KEY = 'admin_weeks_to_show'
const WEEK_OPTIONS = ['1', '2', '3', '4', '6', '8', '12', 'all']

function loadWeeksToShow() {
  const saved = localStorage.getItem(WEEKS_STORAGE_KEY)
  if (saved && WEEK_OPTIONS.includes(saved)) return saved
  return '3'
}

export default function Layout({ children }) {
  const { user, logout } = useAuth()
  const [lang, setLang] = useState('zh')
  const [weeksToShow, setWeeksToShow] = useState(loadWeeksToShow)
  const location = useLocation()
  const isSermonOverviewPage = location.pathname === '/admin'

  const handleWeeksChange = (value) => {
    setWeeksToShow(value)
    localStorage.setItem(WEEKS_STORAGE_KEY, value)
  }

  const toggleLanguage = () => {
    setLang(lang === 'zh' ? 'en' : 'zh')
  }

  const handleLogout = async () => {
    await logout()
  }

  // Determine role level
  const roleHierarchy = { viewer: 1, user: 2, admin: 3, sysadmin: 4 }
  const userLevel = roleHierarchy[user?.role] || 0

  // Page section labels are shown in the nav tabs below

  return (
    <div className="layout">
      <MaintenanceBanner />

      <header className="site-header">
        <div className="site-header-inner">
          <div className="site-header-top">
            <AppBranding lang={lang} showControls={false} />
            <div className="header-right">
              <button type="button" className="btn-language" onClick={toggleLanguage}>
                {lang === 'zh' ? 'EN' : '中'}
              </button>
              <div className="user-info">
                <span className="user-name">{user?.username}</span>
                <span className={`user-role role-${user?.role}`}>
                  {user?.role === 'sysadmin' ? 'SysAdmin' :
                   user?.role === 'admin' ? 'Admin' :
                   user?.role === 'viewer' ? (lang === 'zh' ? '唯讀' : 'View-only') :
                   'User'}
                </span>
              </div>
              <button type="button" className="btn-logout" onClick={handleLogout}>
                {t('logout', lang)}
              </button>
              <div className="header-brand-controls">
                <ThemeToggle />
                <img
                  className="app-branding-logo"
                  src="/hkmbc-logo.gif"
                  alt="HKMBC logo"
                  width={44}
                  height={44}
                />
              </div>
            </div>
          </div>

          <nav className="site-nav" aria-label={lang === 'zh' ? '主選單' : 'Main menu'}>
            <div className="nav-links">
              {user?.role !== 'viewer' && (
                <NavLink
                  to="/event"
                  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                  end
                >
                  {t('events', lang)}
                </NavLink>
              )}
              {user?.role !== 'viewer' && (
                <NavLink
                  to="/ministry"
                  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                  end
                >
                  {t('ministryReport', lang)}
                </NavLink>
              )}
              {userLevel >= 1 && (
                <NavLink
                  to="/admin"
                  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                  end
                >
                  {t('adminPanel', lang)}
                </NavLink>
              )}
              {userLevel >= 1 && (
                <NavLink
                  to="/admin/events"
                  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                >
                  {t('eventsOverview', lang)}
                </NavLink>
              )}
              {userLevel >= 4 && (
                <NavLink
                  to="/sysadmin"
                  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                >
                  {t('sysAdminPanel', lang)}
                </NavLink>
              )}
            </div>
            {isSermonOverviewPage && userLevel >= 1 && (
              <label className="nav-weeks-control">
                <span>{t('weeksToShow', lang)}</span>
                <select
                  value={weeksToShow}
                  onChange={(e) => handleWeeksChange(e.target.value)}
                  aria-label={t('weeksToShow', lang)}
                >
                  {WEEK_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt === 'all' ? t('weeksAll', lang) : opt}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="main">
        <div className="container">
          {React.cloneElement(children, { lang, weeksToShow })}
        </div>
      </main>

      {/* Footer */}
      <footer className="footer">
        <p>© {new Date().getFullYear()} {t('appTitle', lang)} · {t('appSubtitle', lang)}</p>
      </footer>
    </div>
  )
}